import isNil from 'lodash/isNil';
import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.socialListeningApiUrl, {
  camelizeKeys: true,
});

export async function getTopicStats(
  { organizationId, topicId, searchFilters, useEstimates, scale },
  config = {},
) {
  const url = `/organizations/${organizationId}/stats`;
  const payload = { topicId, searchFilters, useEstimates, scale };
  return http.post(url, payload, { ...config });
}

export async function getStatsPrediction(
  { organizationId, searchFilters, useEstimates, scale },
  config = {},
) {
  const url = `/organizations/${organizationId}/stats/prediction`;
  const payload = { searchFilters, useEstimates, scale };

  return http.post(url, payload, { ...config });
}

export async function getTopicsUsage(organizationId, config = {}) {
  const url = `/organizations/${organizationId}/topics/usage`;
  return http.get(url, config);
}

export async function getTopics(organizationId, summary, config = {}) {
  const url = `/organizations/${organizationId}/topics`;
  return http.get(url, { ...config, summary });
}

export async function getTopic({ organizationId, topicId }, config = {}) {
  const url = `/organizations/${organizationId}/topics/${topicId}`;
  return http.get(url, config);
}

export async function updateTopic({ organizationId, topicId }, payload, config = {}) {
  const url = `/organizations/${organizationId}/topics/${topicId}`;
  return http.patch(url, payload, config);
}

export async function deleteTopic({ organizationId, topicId }, config = {}) {
  const url = `/organizations/${organizationId}/topics/${topicId}`;
  return http.delete(url, config);
}

export async function createTopic(
  { organizationId, name, selectedBrandId, searchBody, meta, suggestionId },
  config = {},
) {
  const url = `/organizations/${organizationId}/topics`;
  const payload = { name, selectedBrandId, searchBody, meta, suggestionId };
  return http.post(url, payload, config);
}

export async function getTopKeywords({ organizationId, payload, useEstimates }, config = {}) {
  const fullPayload = { ...payload, ...useEstimates };
  const url = `/organizations/${organizationId}/top_keywords`;
  return http.post(url, fullPayload, config);
}

export async function getSearchResults({ organizationId, payload }, config = {}) {
  const url = `/organizations/${organizationId}/search`;
  return http.post(url, payload, config);
}

export async function getRestrictedContentCount({ organizationId, payload }, config = {}) {
  const url = `/organizations/${organizationId}/search/restricted`;
  return http.post(url, payload, config);
}

export async function getTimeSeriesPostVolume(
  { organizationId, payload, useEstimates },
  config = {},
) {
  const fullPayload = { ...payload, ...useEstimates };
  const url = `/organizations/${organizationId}/time_series/post_volume`;
  return http.post(url, fullPayload, config);
}

export async function getParseTree({ organizationId, payload }, config = {}) {
  const url = `/organizations/${organizationId}/search/parse_tree`;
  return http.post(url, payload, config);
}

export async function getPostVolume({ organizationId, payload, useEstimates }, config = {}) {
  const fullPayload = { ...payload, ...useEstimates };
  const url = `/organizations/${organizationId}/post_volume`;
  return http.post(url, fullPayload, config);
}

export async function getVisualTrends({ organizationId, payload }, config = {}) {
  const url = `/organizations/${organizationId}/visual_trends`;
  return http.post(url, payload, config);
}

export async function getTrendGroup(
  { organizationId, trendId, offset = null, limit = null },
  config = {},
) {
  const url = `/organizations/${organizationId}/visual_trends/${trendId}`;
  let requestConfig;
  if (isNil(offset) || isNil(limit)) {
    requestConfig = config;
  } else {
    requestConfig = merge({ params: { offset, limit } }, config);
  }
  return http.get(url, requestConfig);
}

export async function getTopicMonitors({ organizationId, topicId }, config = {}) {
  const url = `/organizations/${organizationId}/topics/${topicId}/monitors`;
  return http.get(url, config);
}

export async function createTopicMonitor(
  { organizationId, topicId, alertRecipients, muted, sensitivity },
  config = {},
) {
  const url = `/organizations/${organizationId}/topics/${topicId}/monitors`;
  return http.post(
    url,
    {
      alertRecipients,
      muted,
      sensitivity,
    },
    config,
  );
}

export async function updateTopicMonitor(
  { organizationId, topicId, topicMonitorId, alertRecipients, muted, sensitivity },
  config = {},
) {
  const url = `/organizations/${organizationId}/topics/${topicId}/monitors/${topicMonitorId}`;
  return http.patch(
    url,
    {
      alertRecipients,
      muted,
      sensitivity,
    },
    config,
  );
}

export async function getTopicMonitorEvents(
  { organizationId, topicId, monitorId, startDate, endDate },
  config = {},
) {
  const url = `/organizations/${organizationId}/topics/${topicId}/monitors/${monitorId}/monitor_events`;
  const requestConfig = merge({ params: { startDate, endDate } }, config);
  return http.get(url, requestConfig);
}

export async function getTeaserTopKeywords({ organizationId, payload, useEstimates }, config = {}) {
  const fullPayload = { ...payload, ...useEstimates };
  const url = `/teasers/organizations/${organizationId}/top_keywords`;
  return http.post(url, fullPayload, config);
}

export async function getTeaserStats(
  { organizationId, topicId, searchFilters, useEstimates, scale },
  config = {},
) {
  const url = `/teasers/organizations/${organizationId}/stats`;
  const payload = { topicId, searchFilters, useEstimates, scale };
  return http.post(url, payload, { ...config });
}

export async function getTeaserSearchResults({ organizationId, payload }, config = {}) {
  const url = `/teasers/organizations/${organizationId}/search`;
  return http.post(url, payload, config);
}

export async function getTeaserPostVolume({ organizationId, payload, useEstimates }, config = {}) {
  const fullPayload = { ...payload, ...useEstimates };
  const url = `/teasers/organizations/${organizationId}/post_volume`;
  return http.post(url, fullPayload, config);
}

export async function getTeaserVisualTrends({ organizationId, payload }, config = {}) {
  const url = `/teasers/organizations/${organizationId}/visual_trends`;
  return http.post(url, payload, config);
}

export async function getTeaserTrendGroup(
  { organizationId, trendId, offset = null, limit = null },
  config = {},
) {
  const url = `/teasers/organizations/${organizationId}/visual_trends/${trendId}`;
  let requestConfig;
  if (isNil(offset) || isNil(limit)) {
    requestConfig = config;
  } else {
    requestConfig = merge({ params: { offset, limit } }, config);
  }
  return http.get(url, requestConfig);
}

export async function getTeaserTimeSeriesPostVolume(
  { organizationId, payload, useEstimates },
  config = {},
) {
  const fullPayload = { ...payload, ...useEstimates };
  const url = `/teasers/organizations/${organizationId}/time_series/post_volume`;
  return http.post(url, fullPayload, config);
}

export async function getSuggestedTopicsForOrganization(organizationId, config = {}) {
  const url = `/organizations/${organizationId}/suggested_topics`;
  return http.get(url, config);
}

export async function createTopicFromSuggestion(
  { organizationId, suggestionId, sourceCreated },
  config = {},
) {
  const url = `/organizations/${organizationId}/suggestions/${suggestionId}/topics`;
  return http.post(url, { sourceCreated }, config);
}

export async function getWebzCounts(searchFilters, config = {}) {
  const url = `/webz/counts`;
  const payload = { searchFilters };
  return http.post(url, payload, { ...config });
}

// Comparisons Endpoints

export async function getComparisons(organizationId, config = {}) {
  return http.get(`/organizations/${organizationId}/topic_comparisons`, config);
}

export async function getComparison(organizationId, comparisonId, config = {}) {
  const url = `/organizations/${organizationId}/topic_comparisons/${comparisonId}`;
  return http.get(url, config);
}

export async function deleteComparison(organizationId, comparisonId, config = {}) {
  return http.delete(`/organizations/${organizationId}/topic_comparisons/${comparisonId}`, config);
}

export async function createComparison({ organizationId, name, topicIds }, config = {}) {
  return http.post(
    `/organizations/${organizationId}/topic_comparisons`,
    { name, topicIds },
    config,
  );
}

export async function updateComparison(
  { organizationId, comparisonId, name, topicIds },
  config = {},
) {
  return http.patch(
    `/organizations/${organizationId}/topic_comparisons/${comparisonId}`,
    { name, topicIds },
    config,
  );
}

export async function fetchNaturalLanguageSearchContent(
  { sources, userRequest, visualFeatures },
  config = {},
) {
  return http.get('/natural_language_search/content', {
    ...config,
    params: { sources, userRequest, visualFeatures },
    paramsSerializer: { indexes: null },
  });
}

export async function postCommentReply(
  { organizationId, sourceId, source, caption, parentId, authorName, authorProfilePictureUrl },
  config = {},
) {
  const url = `/organizations/${organizationId}/mentions/comment_reply`;
  return http.post(
    url,
    { sourceId, source, caption, parentId, authorName, authorProfilePictureUrl },
    config,
  );
}

export async function updateSentiment(
  { organizationId, sourceAndId, sentimentLabel },
  config = {},
) {
  const url = `/organizations/${organizationId}/mentions/sentiment`;
  return http.patch(url, { sourceAndId, sentimentLabel }, config);
}
